import { camelCase } from 'lodash';
import dibsLogo from '../images/dibs-logo.svg';
import heroImg from "../images/LoginImage.svg";
import brainhiveLogo from '../images/brainhive.svg';
import themesById from '../constants/themes.json';
import BookFS from '../api/bookFS';
import config from '../api/config';
import { pWaCompatUtil } from './pWaCompatUtil';
import { CSSProp } from 'styled-components';

interface Itheme {
    name: string;
    logoSrc: string;
    primaryBackground: string;
    primaryDark: string;
    primaryLight:string;
    primaryFontColor: string;
    secondaryBackground: string;
    secondaryButtonStyle?: CSSProp; // generated on front end
    warningButtonStyle?: CSSProp;
    primaryButtonStyle?: CSSProp;
    successButtonStyle?: CSSProp;
    primaryIconOnlyButtonStyle?: CSSProp;
    secondaryDark: string;
    secondaryLight: string;
    secondaryFontColor: string;
    warningBackground: string;
    warningDark: string;
    warningLight: string;
    warningFontColor: string;
    successBackground: string;
    successDark: string;
    successLight: string;
    successFontColor: string;
    error: string;
    manifestUrl: string;
}

export const themeProvider = {
    activeTheme: themesById['default'] as Itheme,
    themesById: undefined as {[key:string]: Itheme} | undefined, // only populated if we have received updated themes from the API
    initTheme: function () {
        // Always resolve the brand from the CURRENT DOMAIN on every load: use the live API themes
        // if we received them, otherwise the bundled fallback (which ships every brand). A
        // previously-persisted activeTheme is intentionally NOT used for the branding decision -
        // it can be stale (e.g. a 'default' theme persisted before the fallback carried this
        // brand, or when the themes API is unreachable) and would otherwise override the domain's
        // real brand on every subsequent visit. selectThemeBySubdomain is synchronous against the
        // bundled JSON, so this is just as fast as reading the persisted value.
        try {
            if (this.themesById) {
                console.log('init theme from API');
                return this.setTheme(this.selectThemeBySubdomain(this.themesById));
            }
            console.log('init theme by subdomain (bundled fallback)');
            return this.setTheme(this.selectThemeBySubdomain());
        } catch (error) {
            console.error('error initializing theme', error);
            return Promise.resolve();
        }
    },
    // Returns an explicit theme key to force from the ?theme=<key> query param (dev/testing only),
    // or null. Does NOT include config.DefaultTheme - that is only a last-resort fallback applied
    // in selectThemeBySubdomain AFTER subdomain matching, so it never overrides a customer's own
    // branded subdomain (e.g. viewer.brainhive.com must stay brainhive, not the default).
    getThemeOverride: function (): string | null {
        try {
            const q = new URLSearchParams(window.location.search).get('theme');
            if (q) {
                return q;
            }
        } catch (e) {
            // URLSearchParams unavailable or bad URL - ignore and fall through
        }
        return null;
    },
    selectThemeBySubdomain: function (themes: {[key:string]: Itheme} = themesById) {
        // 1) explicit ?theme=<key> override (dev/testing), only if it names a real theme
        const override = this.getThemeOverride();
        if (override && themes[override]) {
            return themes[override];
        }
        // 2) match by subdomain: the theme key appears somewhere in the URL
        let subdomain = 'default';
        let hasTheme = false;
        Object.keys(themes).forEach((key) => {
            hasTheme = window.location.href.search(key) !== -1;
            if (hasTheme) {
                subdomain = key;
            }
        });
        // 3) no subdomain matched (e.g. the bare *.azurewebsites.net dev URL) -> use the
        //    per-env config.DefaultTheme if it names a real theme. Customer subdomains that
        //    matched in step 2 are untouched.
        if (subdomain === 'default') {
            const fallback = (config as any).DefaultTheme;
            if (fallback && themes[fallback]) {
                return themes[fallback];
            }
        }
        return themes[subdomain];
    },
    normalizeTheme: function (theme: Itheme) {
        // if the theme does not have an attribute, fall back to the default attribute
        const defaultTheme = this.selectThemeBySubdomain();
        Object.keys(defaultTheme).forEach((key) => {
            theme = { ...theme, [key]: theme[key as keyof Itheme] || defaultTheme[key as keyof Itheme] };
        });
        let logo = theme.logoSrc;
        let hero = theme.heroImage;
        if (theme.name === 'brainhive') {
            logo = brainhiveLogo;
            hero = heroImg;
        }
        if (theme.name === 'default') {
            logo = dibsLogo;
            hero = heroImg;
        }
        const warningButtonStyle = {
            'background-color': theme.warningBackground,
            'border-bottom-color': theme.warningDark,
            color: theme.warningFontColor,
            '&:active': {
                'background-color': theme.warningDark,
                'border-bottom-color': '#000'
            }
        };
        const primaryButtonStyle = {
            'background-color': theme.primaryBackground,
            'border-bottom-color': theme.primaryDark,
            color: theme.primaryFontColor,
            '&:active': {
                'background-color': theme.primaryDark,
                'border-bottom-color': '#000'
            }
        };
        const secondaryButtonStyle = {
            'background-color': theme.secondaryBackground,
            'border-bottom-color': theme.secondaryDark,
            color: theme.secondaryFontColor,
            '&:active': {
                'background-color': theme.secondaryDark,
                'border-bottom-color': '#000'
            }
        };
        const successButtonStyle = {
            'background-color': theme.successBackground,
            'border-bottom-color': theme.successDark,
            color: theme.successFontColor,
            '&:active': {
                'background-color': theme.successDark,
                'border-bottom-color': '#000'
            }
        };
        const primaryIconOnlyButtonStyle = {
            color: theme.primaryBackground,
            '&:active': {
                color: theme.primaryDark
            }
        };
        return {
            ...theme,
            logo,
            hero,
            warningButtonStyle,
            primaryButtonStyle,
            secondaryButtonStyle,
            successButtonStyle,
            primaryIconOnlyButtonStyle
        };
    },
    setTheme: function (theme: Itheme) {
        // console.log('persisting theme', theme); // TODO why are we persisting so many times?
        this.activeTheme = this.normalizeTheme(theme);
        const manifestPlaceholder = document
        .querySelector('#my-manifest-placeholder');
        if (manifestPlaceholder){
            manifestPlaceholder.setAttribute('href', this.activeTheme.manifestUrl);
            pWaCompatUtil();
        }
        return BookFS.fs.setItem('activeTheme', theme);
    },
    checkUpdatedThemes: function () {
        // V3: themes served by the maintained API (was Admin's /home/getviewerthemes).
        // See util/viewerthemes in the DiBS API. Decouples theming from the Admin app.
        const URL = `${config.API.Main}/util/viewerthemes`;
        var request = new Request(URL, { method: 'GET' });

        return fetch(request)
            .then((response) => {
                if (response.status !== 200) {
                    throw response;
                }
                return response.json();
            })
            .then((res) => {
                this.themesById = res.reduce((themes: any, t: any) => {
                    return {
                        ...themes,
                        [t.Name]: Object.keys(t).reduce(
                            (camelCaseKeys, rawKey) => {
                                return {
                                    ...camelCaseKeys,
                                    [camelCase(rawKey)]: t[rawKey]
                                };
                            },
                            {}
                        )
                    };
                }, {});
                // console.log('themes', JSON.stringify(this.themesById))
                return this.initTheme();
            })
            .catch((error) => {
                console.error('unable to check for updated theme', error);
            });
    }
};
